import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { externalSupabase } from "@/lib/externalSupabase";
import { useToast } from "@/hooks/use-toast";

const REASONS = [
  { value: "guest", label: "Guest: Question about my search/audit" },
  { value: "host", label: "Host: I want to verify my property on Bnbxo" },
  { value: "general", label: "General: Partnership or Press" },
];

const ContactPage = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    reason: "",
    message: "",
  });

  // Suggest a Property state
  const [suggestSubmitted, setSuggestSubmitted] = useState(false);
  const [suggestSubmitting, setSuggestSubmitting] = useState(false);
  const [suggestForm, setSuggestForm] = useState({
    email: "",
    airbnb_url: "",
    notes: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.reason || !form.message.trim()) {
      toast({ title: "Please fill in all fields", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await externalSupabase
        .from("contact_inquiries")
        .insert({
          name: form.name.trim(),
          email: form.email.trim(),
          reason: form.reason,
          message: form.message.trim(),
        } as any);

      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      console.error("Contact submit error:", err);
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuggestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suggestForm.email.trim() || !suggestForm.airbnb_url.trim()) {
      toast({ title: "Please enter your email and the Airbnb URL", variant: "destructive" });
      return;
    }

    setSuggestSubmitting(true);
    try {
      const { error } = await externalSupabase
        .from("property_suggestions")
        .insert({
          guest_email: suggestForm.email.trim(),
          airbnb_url: suggestForm.airbnb_url.trim(),
          notes: suggestForm.notes.trim() || null,
        } as any);

      if (error) throw error;
      setSuggestSubmitted(true);
    } catch (err) {
      console.error("Suggest submit error:", err);
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setSuggestSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-6 max-w-xl">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2 text-center">
              Contact Us
            </h1>
            <p className="text-muted-foreground text-center mb-10">
              Have a question or want to work with us? We'd love to hear from you.
            </p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-xl border bg-card p-10 text-center space-y-4"
              >
                <CheckCircle className="w-12 h-12 text-primary mx-auto" />
                <h2 className="font-display text-xl font-semibold text-foreground">
                  Message Received!
                </h2>
                <p className="text-muted-foreground text-sm">
                  We'll get back to you as soon as possible.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="rounded-xl border bg-card p-8 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    maxLength={100}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    maxLength={255}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Reason for Contact</Label>
                  <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us more…"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    maxLength={2000}
                    rows={5}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? "Sending…" : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>

          {/* Suggest a Property */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-16"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <MapPin className="w-5 h-5 text-primary" />
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground text-center">
                Suggest a Property
              </h2>
            </div>
            <p className="text-muted-foreground text-center mb-8 text-sm md:text-base">
              Can't find the owner? Drop the link here and we'll hunt it down for you.
            </p>

            {suggestSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-xl border bg-card p-10 text-center space-y-4"
              >
                <CheckCircle className="w-12 h-12 text-primary mx-auto" />
                <h2 className="font-display text-xl font-semibold text-foreground">
                  We're on it!
                </h2>
                <p className="text-muted-foreground text-sm">
                  We'll look into this property and notify you when we find the owner.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSuggestSubmit} className="rounded-xl border bg-card p-8 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="suggest-email">Your Email</Label>
                  <Input
                    id="suggest-email"
                    type="email"
                    placeholder="you@example.com"
                    value={suggestForm.email}
                    onChange={(e) => setSuggestForm({ ...suggestForm, email: e.target.value })}
                    maxLength={255}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="suggest-url">Airbnb URL</Label>
                  <Input
                    id="suggest-url"
                    type="url"
                    placeholder="https://www.airbnb.com/rooms/..."
                    value={suggestForm.airbnb_url}
                    onChange={(e) => setSuggestForm({ ...suggestForm, airbnb_url: e.target.value })}
                    maxLength={500}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="suggest-notes">Notes (optional)</Label>
                  <Textarea
                    id="suggest-notes"
                    placeholder="Any extra details — dates, what you've tried, etc."
                    value={suggestForm.notes}
                    onChange={(e) => setSuggestForm({ ...suggestForm, notes: e.target.value })}
                    maxLength={1000}
                    rows={3}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={suggestSubmitting}>
                  {suggestSubmitting ? "Submitting…" : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Submit Property
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
